import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { computeGwp, GiftState } from '../utilities/computeGwp';
import { changeGift, chooseGift as chooseGiftAction, removeGift as removeGiftAction } from '../utilities/cartGiftActions';
import { fetchCart as fetchCartUtility, FetchCartOutput } from '../utilities/fetchCart';
import { Cart, CartContextValue, CartProviderProps, GwpState, GwpProduct, GwpVariant, RemoveGiftResult,  } from '../utilities/types';

// Create the CartContext with default values
const CartContext = createContext<CartContextValue | undefined>(undefined);

/**
 * Custom hook to use the CartContext.
 * @throws {Error} When used outside of CartProvider.
 */
export const useCart = (): CartContextValue => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

/**
 * Provider component for cart context.
 */
export const CartProvider = ({ children }: CartProviderProps) => {
  const [cartToken, setCartToken] = useState<string | null>(null);
  const [cart, setCart] = useState<Cart | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [gwp, setGwp] = useState<GwpState>({
    requiredSpend: 0,
    currentSpend: 0,
    giftState: GiftState.UNAVAILABLE,
    type: '',
    collections: [],
    page: null,
    giftProducts: [],
    selectedGift: null,
    title: '',
    description: ''
  });

  /**
   * Fetches the cart from Storefront API using the cart token.
   */
  const fetchCart = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result: FetchCartOutput = await fetchCartUtility();
      const { token, cart: fetchedCart, freeGiftObjects } = result;

      // Set cart state
      setCart(fetchedCart as Cart);
      setCartToken(token);

      // Compute GWP state
      const computed = computeGwp({ cart: fetchedCart, freeGiftObjects });
      
      // Check if gift should be removed due to vendor mismatch
      if (computed.shouldRemoveGift && computed.selectedGift) {
        console.log('[GWP] Auto-removing gift due to vendor mismatch');
        
        // Store replacement gift info before removal
        const replacementGift = computed.replacementGift;
        
        // If there's a replacement gift, add it
        let cartDrawerHtml: string | undefined = undefined;
        
        if (replacementGift) {
          const isFree = replacementGift.price === 0;
          let properties: Record<string, string | undefined> = {
            // Free (GWP)
            _freeGift: isFree ? 'main' : undefined,
            _vendorRoutingMatchFree: isFree ? replacementGift.vendor : undefined,
            // Paid (GWPrice)
            _promotional: !isFree ? 'true' : undefined,
            _vendorRoutingMatch: !isFree ? replacementGift.vendor : undefined
          };
          try {
            const result = await changeGift({ 
              newGiftVariantId: replacementGift.variantId, 
              vendor: replacementGift.vendor,
              properties
            });
            cartDrawerHtml = result.cartDrawer;
          } catch (err) {
            console.error('[GWP] Failed to add replacement gift:', err);
          }
        } else {
          const result = await removeGiftAction({
            skipDrawerFetch: false
          })
          cartDrawerHtml = result.cartDrawer;
        }

        // Update cart drawer if we have new HTML
        if (cartDrawerHtml) {
          const windowWithCartDrawer = window as any;
          if (windowWithCartDrawer.CartDrawer) {
            console.log('[GWP] Refreshing cart drawer after auto-removal/replacement');
            await windowWithCartDrawer.CartDrawer.updateCart?.({ html: cartDrawerHtml });
          }
        }
        
        // Re-fetch cart after removal (and potential replacement)
        const updatedResult: FetchCartOutput = await fetchCartUtility();
        const { token: updatedToken, cart: updatedCart, freeGiftObjects: updatedGiftObjects } = updatedResult;
        
        // Update cart state
        setCart(updatedCart as Cart);
        setCartToken(updatedToken);
        
        // Recompute GWP state after removal/replacement
        const recomputed = computeGwp({ cart: updatedCart, freeGiftObjects: updatedGiftObjects });
        setGwp({
          ...recomputed,
          title: recomputed.title ?? '',
          description: recomputed.description ?? ''
        });
      } else {
        setGwp({
          ...computed,
          title: computed.title ?? '',
          description: computed.description ?? ''
        });
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error occurred');
      setError(error);
      setCart(null);
      setGwp({
        requiredSpend: 0,
        currentSpend: 0,
        giftState: GiftState.UNAVAILABLE,
        type: '',
        collections: [],
        page: null,
        giftProducts: [],
        selectedGift: null,
        title: '',
        description: ''
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log({ gwp });
  }, [gwp]);

  // Hook into window.fetch to detect cart updates
  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
      const response = await originalFetch(...args);

      // Check if this was a POST request to /cart
      const [url, options] = args;
      const method = options?.method?.toUpperCase() || 'GET';
      const urlString = typeof url === 'string' ? url : url.toString();

      if (method === 'POST' && urlString.includes('/cart')) {
        // Trigger cart refetch after a short delay to ensure cart is updated
        setTimeout(() => {
          if (cartToken) {
            fetchCart();
          }
        }, 0);
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [fetchCart, cartToken]);

  // Initial load: fetch cart
  useEffect(() => {
    let isMounted = true;
    
    const loadCart = async () => {
      if (isMounted) {
        await fetchCart();
      }
    };

    loadCart();

    return () => {
      isMounted = false;
    };
  }, [fetchCart]);

  /**
   * Refetches cart data (e.g. after mutation).
   */
  const refetchCart = useCallback(async () => {
    // Hit AJAX cart to warm state then refresh storefront cart
    await fetch('/cart.js?refresh=true', { credentials: 'same-origin' }).catch(() => {});
    await fetchCart();
  }, [fetchCart]);

  /**
   * Adds the selected gift variant to cart with the _freeGift attribute.
   */
  const chooseGift = useCallback(
    async (variantId: string, properties: Record<string, any> = {}): Promise<{ ok: boolean; cartDrawer?: string }> => {
      setIsLoading(true);
      let cartDrawer: string | undefined = undefined;
      
      try {
        // Extract vendor from GWP products
        const vendor = (() => {
          try {
            const allVariants = (gwp?.giftProducts || []).flatMap((p: GwpProduct) => p?.variants || []);
            const match = allVariants.find((v: GwpVariant) => String(v?.id) === String(variantId));
            return match?.vendor || null;
          } catch (e) {
            console.error('[GWP] Error fetching vendor', e);
            return null;
          }
        })();

        const result = await chooseGiftAction({ variantId, vendor, properties });
        cartDrawer = result.cartDrawer || undefined;
        
        await refetchCart();
        
        return { ok: !!result.ok, cartDrawer };
      } finally {
        setIsLoading(false);
        if (cartDrawer) {
          const windowWithCartDrawer = window as any;
          await windowWithCartDrawer.CartDrawer?.updateCart?.({ html: cartDrawer });
          await windowWithCartDrawer.CartDrawer?.open?.();
        }
      }
    },
    [refetchCart, gwp]
  );

  /**
   * Removes any free gift line from the cart.
   */
  const removeGift = useCallback(async (): Promise<RemoveGiftResult> => {
    setIsLoading(true);
    let cartDrawer: string | undefined = undefined;
    
    try {
      const result = await removeGiftAction();
      cartDrawer = result.cartDrawer || undefined;
      
      await refetchCart();
      console.log('[GWP] removeGift success', { ok: result.ok, cartDrawer });
      
      return { ok: !!result.ok, cartDrawer };
    } finally {
      setIsLoading(false);
      if (cartDrawer) {
        const windowWithCartDrawer = window as any;
        await windowWithCartDrawer.CartDrawer?.updateCart?.({ html: cartDrawer });
        await windowWithCartDrawer.CartDrawer?.open?.();
      }
    }
  }, [refetchCart]);

  const contextValue: CartContextValue = {
    cartToken,
    cart,
    isLoading,
    error,
    refetchCart,
    gwp: {
      ...gwp,
      chooseGift,
      removeGift
    }
  };

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};